<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="(isBoostPlan ? '管理' : '创建') + '创意优投项目'"
    width="680px"
    :close-on-click-modal="false"
    @close="onClose"
    @open="onOpen"
    custom-class="premium-creative-dialog"
  >
    <!-- 源计划 / 账号 -->
    <div class="header-info" v-if="planInfo">
      <div class="info-item plan-item">
        <span class="label">源计划：</span>
        <span class="value" :title="planInfo.planName">{{ planInfo.planName || '-' }}</span>
      </div>
      <div class="info-item account-item">
        <span class="label">账号：</span>
        <span class="value">{{ planInfo.adAccountName || '-' }}</span>
      </div>
    </div>

    <div v-loading="queryLoading">
      <el-form ref="formRef" class="config-form" label-width="280px" label-position="top" :model="form" :rules="formRules">
        <!-- 选择优投素材 -->
        <el-form-item>
          <template #label>
            <div class="section-title">
              <span>选择优投素材</span>
              <span class="sub-title">（可多选，仅显示手动上传的图片/视频）</span>
            </div>
          </template>

          <!-- 素材列表 -->
          <div class="material-list">
            <div
              v-for="item in materialList"
              :key="item.componentId"
              class="material-item"
              :class="{ selected: compIdList.includes(item.componentId) }"
              @click="toggleSelect(item)"
            >
              <div class="material-preview">
                <el-tag class="material-type-text" type="info">{{ item.imageId ? '图片' : '视频' }}</el-tag>

                <el-image
                  v-if="item.imageId && item.imageUrl"
                  v-popover="{ url: item.imageUrl, type: 'image', height: 220 }"
                  style="height: 100%; cursor: pointer"
                  :src="item.imageUrl"
                ></el-image>

                <el-image
                  v-if="item.videoId && item.coverUrl"
                  v-popover="{ url: item.videoUrl, type: 'video', height: 330 }"
                  style="height: 100%; cursor: pointer"
                  :src="item.coverUrl"
                ></el-image>

                <div v-if="compIdList.includes(item.componentId)" class="selected-mark">
                  <i class="el-icon-check"></i>
                </div>
              </div>
              <div class="material-name" :title="item.materialName">{{ item.materialName }}</div>
            </div>

            <div v-if="materialList.length === 0" class="empty-materials">暂无图片/视频素材</div>
          </div>

          <div class="material-footer">
            <span class="selected-count">
              已选
              <span class="highlight-count">{{ compIdList.length }} </span>
              个素材
            </span>
            <el-button type="text" @click="selectAllMaterials">全选</el-button>
          </div>
        </el-form-item>

        <!-- 提价比例 -->
        <el-form-item>
          <template #label>
            <div class="section-title">
              <span>提价比例</span>
              <span class="sub-title">（在源计划出价基础上上调，让优投素材获得更强竞争力）</span>
            </div>
          </template>
          <div class="slider-row">
            <el-slider
              v-model="form.bid_adjustment_rate"
              :step="1"
              :min="10"
              :max="50"
              class="custom-slider"
              :show-tooltip="false"
              @input="onBidAdjSlideInput"
            ></el-slider>
            <div class="slider-input">
              <el-input v-model.number="form.input_bid_adjustment_rate" @blur="onBidAdjRateBlur" placeholder="请输入内容">
                <template slot="append">%</template>
              </el-input>
            </div>
          </div>
          <div class="slider-labels">
            <span>最低 10%</span>
            <span>推荐 20%~30%</span>
            <span>最高 50%</span>
          </div>
          <div class="estimate-text">
            <p>{{ bidInfoText.originText }}</p>
            <p class="highlight-price">{{ bidInfoText.raiseText }}</p>
          </div>
        </el-form-item>

        <!-- 优投日预算 -->
        <el-form-item class="budget-form-item" prop="daily_budget">
          <template #label>
            <div class="section-title">
              <span>优投日预算</span>
              <span class="sub-title">（独立预算，不占用源计划预算）</span>
            </div>
          </template>
          <el-input v-model="form.daily_budget" type="number" placeholder="请输入">
            <template slot="append">元/天</template>
          </el-input>
          <div class="budget-tip">最低日预算：50元；不占用源计划预算，从账户余额独立划拨</div>
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="onClose">取消</el-button>
      <el-button type="primary" @click="onSubmit" :disabled="form.component_id_list.length === 0" :loading="submitLoading">
        {{ isBoostPlan ? '更新' : '新增' }}优投项目
      </el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, set, reactive, computed, nextTick, defineProps, defineEmits } from 'vue'
import { Message } from 'view-design'
import {
  addPremiumCreativePlan,
  updatePremiumCreativePlan,
  getPremiumCreativeAddInfo,
  getPremiumCreativeManageInfo
} from '@/api/ptom.data.model'

const formRules = {
  daily_budget: [
    { required: true, message: '请输入优投日预算', trigger: ['blur'] },
    {
      validator: (rule, value, callback) => {
        if (value < 50) {
          callback(new Error('日预算最低50元/天'))
        }
        if (value > 999999) {
          callback(new Error('日预算最高999999元/天'))
        }
        callback()
      }
    }
  ]
}

const getForm = () => {
  return {
    component_id_list: [],
    bid_adjustment_rate: 20,
    input_bid_adjustment_rate: 20,
    daily_budget: ''
  }
}

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  planInfo: {
    type: Object,
    default: () => null
  }
})

const emit = defineEmits(['update:visible', 'submit'])
const formRef = ref(null)
const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
})

const submitLoading = ref(false)
const queryLoading = ref(false)

const form = reactive(getForm())

const addConfigInfo = ref(null)
const manageConfigInfo = ref(null)
const isBoostPlan = computed(() => props.planInfo?.isBoostPlan)
const ratioMultiplier = computed(() => form.bid_adjustment_rate / 100)
const getRaisedBid = (value) => {
  return (value * ratioMultiplier.value + value).toFixed(2)
}
const configInfo = computed(() => (isBoostPlan.value ? manageConfigInfo.value : addConfigInfo.value))
const compIdList = computed(() => form.component_id_list.map((i) => i.componentId) || [])
const materialList = computed(() => {
  return configInfo.value?.materialList || []
})
const bidInfoText = computed(() => {
  const { bidInfo } = configInfo.value || {}
  if (!bidInfo) return ''

  const { originalBid, originalDeepBid, originalRoiBid, optimizationTypeName, deepOptimizationTypeName } = bidInfo
  let originText = `原计划出价：${originalBid}元/${optimizationTypeName}`
  let raiseText = `${getRaisedBid(originalBid)}元/${optimizationTypeName}`

  if (originalDeepBid && originalDeepBid > 0) {
    originText += `，深度出价：${originalDeepBid}元/${deepOptimizationTypeName}`
    raiseText += `，深度出价：${getRaisedBid(originalDeepBid)}元/${deepOptimizationTypeName}`
  }

  if (originalRoiBid && originalRoiBid > 0) {
    originText += `，ROI系数：${originalRoiBid}/${deepOptimizationTypeName}`
    raiseText += `，ROI系数：${(originalRoiBid * (1 - ratioMultiplier.value)).toFixed(4)}/${deepOptimizationTypeName}`
  }
  return {
    originText,
    raiseText: `预计出价上调：${raiseText}`
  }
})

const onOpen = async () => {
  queryLoading.value = true
  if (isBoostPlan.value) {
    const data = await getPremiumCreativeManageInfo({ planId: props.planInfo.planId })
    manageConfigInfo.value = data
    const { bidAdjustmentRate, dailyBudget } = data.boostInfo || {}
    const selectedItems = materialList.value.filter((m) => m.selected)
    if (selectedItems?.length) {
      form.component_id_list = selectedItems
    }
    if (dailyBudget) {
      form.daily_budget = dailyBudget / 100
    }

    if (bidAdjustmentRate) {
      form.bid_adjustment_rate = bidAdjustmentRate / 100
    }
  } else {
    const data = await getPremiumCreativeAddInfo({ planId: props.planInfo.planId })
    addConfigInfo.value = data
  }

  queryLoading.value = false
}

const toggleSelect = (item) => {
  const index = form.component_id_list.findIndex((i) => i.componentId === item.componentId)
  if (index > -1) {
    form.component_id_list.splice(index, 1)
  } else {
    form.component_id_list.push(item)
  }
}

const selectAllMaterials = () => {
  form.component_id_list = materialList.value.map((m) => m)
}

const onBidAdjSlideInput = (val) => {
  form.input_bid_adjustment_rate = val
}
const onBidAdjRateBlur = (e) => {
  const num = Number(e.target.value)
  if (isNaN(num)) return
  const normalized = Math.min(50, Math.max(10, num))
  form.input_bid_adjustment_rate = normalized
  form.bid_adjustment_rate = normalized
}

const onClose = () => {
  dialogVisible.value = false
  Object.assign(form, getForm())
  queryLoading.value = false
}

const onSubmit = () => {
  formRef.value.validate((valid) => {
    if (valid) {
      submitLoading.value = true
      const { planId, projectId, adAccountId } = configInfo.value
      const { input_bid_adjustment_rate, ...restForm } = form
      const params = {
        ...restForm,
        component_id_list: restForm.component_id_list.map((i) => ({
          componentId: i.componentId,
          videoId: i.videoId || undefined,
          imageId: i.imageId || undefined,
          componentType: i.componentType
        })),
        daily_budget: Number(form.daily_budget) * 100, // 元转换为分
        bid_adjustment_rate: restForm.bid_adjustment_rate * 100,
        planId,
        projectId,
        adAccountId
      }
      ;(isBoostPlan.value ? updatePremiumCreativePlan : addPremiumCreativePlan)(params)
        .then((data) => {
          if (data) {
            Message.success('提交成功')
            onClose()
            emit('submit')
          }
        })
        .finally(() => {
          submitLoading.value = false
        })
    } else {
      nextTick(() => {
        const errorMsgElement = document.querySelector('.premium-creative-dialog .el-form-item__error')
        if (errorMsgElement) {
          errorMsgElement.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      })
    }
  })
}
</script>

<style lang="scss" scoped>
@import '~common/styles/element-ui/variables.scss';
.premium-creative-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px 24px;
  }

  .config-form {
    height: 400px;
    overflow: auto;
    padding-right: 10px;
  }

  .header-info {
    display: flex;
    justify-content: flex-start;
    padding-bottom: 16px;
    border-bottom: 1px dashed #ebeef5;
    font-size: 14px;
    color: #333;

    .info-item {
      display: flex;
      margin-right: 40px;

      .label {
        color: #606266;
      }
      .value {
        font-weight: 500;
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &.plan-item {
        flex: 1;
      }

      &.account-item {
        margin-right: 0;
        min-width: 200px;
      }
    }
  }

  .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    line-height: 1.4;
    display: inline-flex;
    align-items: center;

    .sub-title {
      font-size: 12px;
      font-weight: normal;
      color: #909399;
      margin-left: 4px;
    }
  }
  .el-form {
    padding-top: 14px;
    .el-form-item__label {
      padding-bottom: 8px;
    }
  }

  .material-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 4px;

    .empty-materials {
      grid-column: 1 / -1;
      text-align: center;
      padding: 30px 0;
      color: #909399;
      background: #f8f9fa;
      border-radius: 4px;
      font-size: 13px;
    }

    .material-item {
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      transition: all 0.2s;

      &.selected {
        border-color: $primary-color;
      }

      .material-preview {
        height: 84px;
        background-color: #f5f7fa;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        overflow: hidden;

        .material-type-text {
          position: absolute;
          font-size: 12px;
          left: 2px;
          top: 2px;
          z-index: 2;
        }

        .preview-img,
        .preview-video {
          height: 84px;
          object-fit: cover;
        }

        .preview-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #fff;
          width: 100%;
          height: 100%;
          justify-content: center;

          &.image {
            background: linear-gradient(135deg, #7b8aff, #5664ff);
          }

          &.image:nth-child(even) {
            background: linear-gradient(135deg, #ff7bc6, #ff56a1);
          }

          &.video {
            background: linear-gradient(135deg, #2a2d43, #1e2030);
          }

          i {
            font-size: 20px;
            margin-bottom: 4px;
          }
        }

        .selected-mark {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 20px;
          height: 20px;
          background-color: $primary-color;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

          i {
            color: #fff;
            font-size: 12px;
            font-weight: bold;
          }
        }
      }

      .material-name {
        padding: 2px 4px;
        font-size: 12px;
        color: #606266;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        background-color: #fff;
      }
    }
  }

  .material-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    font-size: 13px;
    color: #606266;
    background: #fafafa;
    border-radius: 4px;
    padding: 0 4px;

    .selected-count {
      .highlight-count {
        color: $primary-color;
        font-weight: bold;
        margin: 0 2px;
      }
    }

    .el-button {
      padding: 4px 0;
      font-size: 13px;
    }
  }

  .slider-row {
    display: flex;
    align-items: center;
    margin-top: 4px;
    margin-bottom: 10px;

    .custom-slider {
      flex: 1;
      margin-right: 24px;
    }

    .slider-input {
      ::v-deep .el-input {
        width: 116px;
        line-height: 30px;
      }
    }
  }

  .slider-labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #909399;
    margin-top: -12px;
  }

  .estimate-text {
    margin-top: 12px;
    font-size: 12px;

    .highlight-price {
      font-weight: bold;
      color: $primary-color;
    }
  }

  .budget-form-item {
    margin-bottom: 0;
    margin-top: 24px;

    .budget-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 8px;
      line-height: 1.4;
    }
  }
}
</style>
